import HeliusGate from "../components/HeliusGate";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { SpinnerGapIcon, GraphIcon, BankIcon, HardHatIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import { fetchFundingWeb } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { HitRateBadge, useDeployerStatsBatch } from "../components/HitRateBadge";
import { FollowBuilderButton } from "../components/FollowBuilderButton";

const GOOD = "#00E599", WARN = "#F0A020", BAD = "#FF3366";
const short = (a) => `${a.slice(0, 4)}…${a.slice(-4)}`;
const fmtMcap = (v) => (!v ? "—" : v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}K` : `$${Math.round(v)}`);

const funderColor = (t) => (t === "cex" ? GOOD : t === "deployer" ? BAD : WARN);

const WebCard = ({ web, stats }) => (
  <div className="border border-white/10 bg-card p-4" data-testid={`funding-web-${web.funder}`}>
    <div className="flex items-center gap-2.5 flex-wrap pb-3 border-b border-white/8">
      <BankIcon size={18} style={{ color: funderColor(web.funderType) }} weight="fill" />
      <a href={`https://solscan.io/account/${web.funder}`} target="_blank" rel="noopener noreferrer" className="font-mono text-sm font-bold hover:text-neon transition-colors">
        {short(web.funder)} <ArrowSquareOutIcon size={11} className="inline" />
      </a>
      <span className="px-1.5 py-0.5 font-mono text-[10px] font-bold" style={{ background: `${funderColor(web.funderType)}1a`, border: `1px solid ${funderColor(web.funderType)}`, color: funderColor(web.funderType) }}>
        {(web.funderLabel || "unknown source").toUpperCase()}
      </span>
      <span className="font-mono text-[11px] text-muted-foreground">bankrolled {web.deployers.length} deployers</span>
      {web.funderDeployedTokens > 0 && <span className="font-mono text-[11px]" style={{ color: BAD }}>+ deployed {web.funderDeployedTokens} tokens itself</span>}
    </div>
    <div className="mt-3 space-y-2">
      {web.deployers.map((d) => (
        <div key={d.deployer} className="flex items-center gap-3 flex-wrap" data-testid={`funding-deployer-${d.deployer}`}>
          <span className="w-4 border-t" style={{ borderColor: `${funderColor(web.funderType)}66` }} />
          <HardHatIcon size={13} className="text-muted-foreground shrink-0" />
          <a href={`https://solscan.io/account/${d.deployer}`} target="_blank" rel="noopener noreferrer" className="font-mono text-xs hover:text-neon transition-colors">{short(d.deployer)}</a>
          {d.amountSol != null && <span className="font-mono text-[10px] text-muted-foreground">seeded {d.amountSol} SOL</span>}
          <span className="font-mono text-[10px] text-muted-foreground">{d.tokens} token{d.tokens === 1 ? "" : "s"}</span>
          {d.bestPeakMcap > 0 && <span className="font-mono text-[10px]" style={{ color: d.bestPeakMcap >= 1e6 ? GOOD : "#8A8F98" }}>best peak {fmtMcap(d.bestPeakMcap)}</span>}
          <HitRateBadge stats={stats[d.deployer]} compact />
          <span className="font-mono text-[10px] text-muted-foreground/70 truncate">{d.symbols.map((s) => `$${s}`).join(" ")}</span>
          {stats[d.deployer]?.proven && <FollowBuilderButton creator={d.deployer} size="sm" />}
        </div>
      ))}
    </div>
  </div>
);

export default function FundingWeb() {
  return (
    <HeliusGate feature="Funding Web">
      <FundingWebInner />
    </HeliusGate>
  );
}

function FundingWebInner() {
  useSeo({
    title: "Funding Web — Deployers Who Share a Money Source | AlphaApeing",
    description: "On-chain map of Solana token deployers bankrolled by the same wallet — CEX-funded, serial-deployer-funded, or linked farms.",
  });
  const { data, isLoading } = useQuery({ queryKey: ["fundingWeb"], queryFn: fetchFundingWeb, refetchInterval: 120000 });
  const webs = data?.webs || [];
  const stats = useDeployerStatsBatch(webs.flatMap((w) => w.deployers.map((d) => d.deployer)));
  // clusters whose deployers actually produce runners float to the top
  const clusterRank = (w) => Math.max(0, ...w.deployers.map((d) => stats[d.deployer]?.runnerCount || 0));
  const sortedWebs = [...webs].sort((a, b) => clusterRank(b) - clusterRank(a));
  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="funding-web-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><GraphIcon size={24} weight="fill" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">FUNDING <span className="text-neon">WEB</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm max-w-3xl leading-relaxed" data-testid="funding-web-explainer">
          Follow the money <span className="text-white font-semibold">before</span> the launch: we trace which wallet seeded every deployer we see.
          When two or more deployers share the same money source, they cluster here — the classic footprint of a token farm running many
          "different" devs from one bankroll. <span className="text-white">CEX-funded</span> is normal-ish; an{" "}
          <span style={{ color: WARN }}>unlabeled wallet</span> funding several deployers, or a funder that{" "}
          <span style={{ color: BAD }}>deploys tokens itself</span>, is your red flag. The web grows automatically as we trace more deployers.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="funding-web-stats">
        {[["Deployers traced", data?.tracedDeployers], ["Shared-source clusters", data?.clusterCount], ["Solo funders", data?.singleFunders]].map(([l, v]) => (
          <div key={l} className="border border-white/10 bg-black/25 px-4 py-2.5 min-w-[140px]">
            <div className="font-mono font-black text-lg text-neon">{v ?? "—"}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><SpinnerGapIcon size={30} className="animate-spin text-neon" /></div>
      ) : webs.length === 0 ? (
        <div className="border border-white/10 bg-card py-16 text-center px-6" data-testid="funding-web-empty">
          <GraphIcon size={36} className="mx-auto text-muted-foreground mb-3" />
          <p className="font-mono text-xs text-muted-foreground max-w-md mx-auto">
            No shared-source clusters yet — the tracer is working through deployers in the background (a few every 5 minutes).
            Clusters appear here the moment two deployers turn out to share a wallet. Check back soon, or open any{" "}
            <Link to="/" className="text-neon hover:underline">coin page</Link> to trace its deployer instantly.
          </p>
        </div>
      ) : (
        <div className="space-y-4">{sortedWebs.map((w) => <WebCard key={w.funder} web={w} stats={stats} />)}</div>
      )}
    </div>
  );
}
